import { hasValue, styles } from "@repo/helpers";
import { Children, CSSProperties, ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import Select, { components, MenuListProps, StylesConfig, ValueContainerProps } from "react-select";
import { List, RowComponentProps, useListRef } from "react-window";

import { InputComponent } from "../../basic/input";
import { DdlValue, WebMarkupProps } from "../types";
import style from "./style.module.scss";

export default function DropDownMarkup(props: WebMarkupProps) {
	const {
		valueProp = "value",
		textProp = "label",
		values,
		loaded = true,
		clearable,
		highlightedValues = new Set<any>(),
		optionStyle,
		maxSelectedDisplay,
		fullLength,
		...selectProps
	} = props;
	const { isMulti, useAllDisplay, leaveOpenAfterSelect, ...inputProps } = selectProps;
	const { id, name, value, disabled, className } = inputProps;

	const [menuIsOpen, setMenuIsOpen] = useState(undefined);
	const onFocused = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			if (isMulti) setMenuIsOpen(true);
			props.onFocused?.(e);
		},
		[props.onUnfocused],
	);
	const onUnfocused = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			setMenuIsOpen(undefined);
			props.onUnfocused?.(e);
		},
		[props.onUnfocused],
	);

	const highlightStyle = useCallback(
		(data: DdlValue) =>
			({
				fontWeight: highlightedValues.has(data.value) ? "bold" : "normal",
				...(optionStyle && optionStyle(data)),
			}) as CSSProperties,
		[highlightedValues, optionStyle],
	);

	const localStyle = useMemo(
		() =>
			({
				container: (base: CSSProperties, state: any) => ({
					...base,
					zIndex: "auto !important",
				}),
				control: (base: CSSProperties, state: any) => ({
					...base,
					// match with the menu
					borderRadius: !loaded ? "4px 0 0 4px" : 4,
					borderRight: !loaded ? 0 : base.borderRight,
				}),
				menuPortal: (base: CSSProperties, state: any) => ({
					...base,
					zIndex: 9999,
					pointerEvents: state.isOpen ? "auto" : "none",
				}),
				menu: (base: CSSProperties, state: any) => ({
					...base,
					zIndex: 9999,
					pointerEvents: "auto",
				}),
				option: (base: CSSProperties, { data }) => ({
					...base,
					...highlightStyle(data),
					zIndex: 10000,
				}),
			}) as StylesConfig<DdlValue>,
		[highlightStyle, loaded],
	);
	const fullStyle = useMemo(() => ({ ...localStyle }), [localStyle]);

	const SelectComponents = useMemo(
		() => ({
			ValueContainer,
			MenuList,
			Menu: (props: any) => (
				<div
					onPointerDown={(e) => e.stopPropagation()}
					onMouseDown={(e) => e.stopPropagation()}
					onClick={(e) => e.stopPropagation()}
					onWheel={(e) => e.stopPropagation()}
				>
					<components.Menu {...props} />
				</div>
			),
		}),
		[],
	);

	const vals = useMemo(() => {
		if (valueProp !== undefined && textProp !== undefined && values && values.map) {
			return values.map((val: any) => {
				return {
					value: val[valueProp],
					label: val[textProp],
				};
			});
		}
		return values;
	}, [values, valueProp, textProp]);

	const minimizingValues = isMulti && hasValue(maxSelectedDisplay) && (value as []).length >= maxSelectedDisplay;

	const hideSelectedOption = props.hideSelected ?? isMulti;
	const hideSelected = hideSelectedOption && !minimizingValues && !useAllDisplay;

	//NOTE: maybe turn of show selected if minimizing display??? will need to figure out how it works
	const fullClassName = styles(style.selectControl, fullLength && style.fullLength, className);
	const menuPortalTarget = typeof document !== "undefined" ? (document.body as HTMLElement) : null;

	const menuPosition = "fixed";

	return (
		<InputComponent {...inputProps}>
			<Select
				{...selectProps}
				styles={fullStyle}
				className={fullClassName}
				classNamePrefix="react-select"
				menuShouldBlockScroll
				captureMenuScroll={false}
				menuPortalTarget={menuPortalTarget}
				menuPosition={menuPosition}
				isDisabled={disabled || !loaded}
				name={name || id}
				components={SelectComponents}
				options={vals}
				isLoading={!loaded}
				onFocus={onFocused}
				onBlur={onUnfocused}
				value={value || null}
				isClearable={clearable}
				closeMenuOnSelect={!leaveOpenAfterSelect}
				hideSelectedOptions={hideSelected}
				//@ts-ignore
				minimizingValues={minimizingValues}
				useAllDisplay={useAllDisplay}
				isMulti={isMulti}
				menuIsOpen={menuIsOpen}
			/>
		</InputComponent>
	);
}

const OPTION_HEIGHT = 35;
const VIRTUALIZATION_THRESHOLD = 100;

function VirtualizedMenuList(props: MenuListProps<DdlValue, boolean>) {
	const { children, maxHeight, innerProps, innerRef, getValue, options } = props;
	const listInnerProps = { ...innerProps, onResize: undefined };
	const items = Children.toArray(children) as ReactElement[];
	const focusedIndex = items.findIndex((item) => item.props.isFocused);
	const listRef = useListRef();
	const selected = getValue()[0];
	const selectedIndex = selected ? options.indexOf(selected) : 0;
	const scrollIndex = focusedIndex >= 0 ? focusedIndex : selectedIndex;

	useEffect(() => {
		const element = listRef.current?.element ?? null;
		if (typeof innerRef === "function") innerRef(element);
	}, [innerRef, listRef]);

	useEffect(() => {
		if (scrollIndex >= 0) listRef.current?.scrollToRow({ index: scrollIndex, align: "smart" });
	}, [scrollIndex, listRef]);

	const height = Math.min(maxHeight, Math.max(OPTION_HEIGHT, items.length * OPTION_HEIGHT));

	return (
		<div
			style={{
				height,
				maxHeight,
				overflow: "hidden",
				padding: 0,
			}}
		>
			<List
				{...listInnerProps}
				defaultHeight={height}
				listRef={listRef}
				rowCount={items.length}
				rowHeight={OPTION_HEIGHT}
				rowProps={{ items }}
				rowComponent={VirtualizedOption}
				style={{ ...listInnerProps.style, height, width: "100%", overflow: "auto" }}
				overscanCount={5}
			></List>
		</div>
	);
}

function MenuList(props: MenuListProps<DdlValue, boolean>) {
	return props.options.length > VIRTUALIZATION_THRESHOLD ? (
		<VirtualizedMenuList {...props} />
	) : (
		<components.MenuList {...props} />
	);
}

function VirtualizedOption({
	index,
	style: rowStyle,
	items,
	ariaAttributes,
}: RowComponentProps<{ items: ReactElement[] }>) {
	return (
		<div {...ariaAttributes} style={rowStyle}>
			{items[index]}
		</div>
	);
}

/**
 * This needs to be defined outside of normal markup
 * Needs to explicitly pass in "minimizingValues" as we need access to it, and this is the only way it gets picked up
 * ts-ignore is so it compiles in spite of this
 * @param args
 * @returns
 */
function ValueContainer(args: ValueContainerProps<DdlValue>) {
	const { children, selectProps, getValue } = args;
	//@ts-ignore
	const { minimizingValues, useAllDisplay, options } = selectProps;
	const value = getValue();
	const allChildren = Children.toArray(children) as any[];
	const numSelected = value.length;
	let selectedValues = allChildren.slice(0, numSelected) as any;
	//Will have an Input component, and may have a Placeholder
	//Always comes after the values, if any
	const otherChildren = allChildren.slice(numSelected);
	if (minimizingValues) {
		const plural = numSelected === 1 ? "" : "s";
		selectedValues = `${numSelected} item${plural}...`;
	}

	if (useAllDisplay && numSelected == options.length) selectedValues = "[ALL]";

	return (
		<components.ValueContainer {...args}>
			{selectedValues}
			{otherChildren}
		</components.ValueContainer>
	);
}
